import React, { Fragment } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";

const SupplyChainPage: React.FC = () => {
    return (
        <Fragment>
            <Helmet>
                <title>Supply Chain Solutions | AquarLabs</title>
                <meta name="description" content="Optimize your supply chain with AquarLabs' intelligent ERP solutions. Enhance procurement, logistics, inventory management, and supplier collaboration." />
                <meta name="keywords" content="supply chain management, SCM, procurement, logistics, inventory management, supplier management, warehouse management, AquarLabs" />
            </Helmet>
            <div className="about-page inner-page">
                <div className="body_wrap o-clip">
                    <Header />
                    <main>
                        <PageTitle pageTitle="Supply Chain Solutions" pagesub="Supply Chain" parentPage="Solutions" parentLink="/service" />

                        {/* Hero Section */}
                        <section className="service-hero-section" style={{ padding: "80px 0", background: "linear-gradient(135deg, rgba(252, 144, 56, 0.1) 0%, rgba(0, 2, 15, 0.8) 100%)" }}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-8">
                                        <h1 style={{ fontSize: "3rem", marginBottom: "20px", color: "#fff" }}>Supply Chain Solutions</h1>
                                        <p style={{ fontSize: "1.25rem", color: "rgba(255,255,255,0.8)", lineHeight: "1.8" }}>
                                            Build resilient, efficient supply chains with intelligent automation and real-time visibility. Our solutions help you optimize procurement, logistics, and inventory across your entire value chain.
                                        </p>
                                        <Link to="/contact-us" className="thm-btn agency-btn" style={{ marginTop: "30px", display: "inline-block" }}>
                                            <span className="text">Get Started</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Overview Section */}
                        <section style={{ padding: "80px 0" }}>
                            <div className="container">
                                <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px" }}>Overview</span>
                                <h2 style={{ fontSize: "2.5rem", margin: "15px 0 30px", color: "#fff" }}>End-to-End Supply Chain Visibility</h2>
                                <p style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "rgba(255,255,255,0.8)" }}>
                                    AquarLabs' Supply Chain solutions provide complete visibility from raw materials to final delivery. Our platform leverages AI and IoT to predict disruptions, optimize inventory levels, and streamline logistics operations across complex global networks.
                                </p>
                                <p style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "rgba(255,255,255,0.8)", marginTop: "20px" }}>
                                    Whether you're managing multi-tier suppliers, coordinating warehouse operations, or optimizing transportation routes, our solutions help you reduce costs, improve service levels, and build supply chain resilience for an uncertain world.
                                </p>
                            </div>
                        </section>

                        {/* Services Section */}
                        <section style={{ padding: "80px 0", background: "rgba(0,2,15,0.3)" }}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-4">
                                        <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase" }}>Our Capabilities</span>
                                        <h2 style={{ fontSize: "2rem", margin: "15px 0", color: "#fff" }}>Complete Supply Chain Suite</h2>
                                    </div>
                                    <div className="col-lg-8">
                                        <div className="row">
                                            {[
                                                {
                                                    title: "Procurement & Sourcing",
                                                    desc: "Strategic sourcing and automated procurement workflows.",
                                                    items: ["Supplier Management", "RFQ & Auction Platforms", "Contract Management"]
                                                },
                                                {
                                                    title: "Inventory Optimization",
                                                    desc: "Right inventory, right place, right time.",
                                                    items: ["Demand Planning", "Safety Stock Optimization", "Multi-location Management"]
                                                },
                                                {
                                                    title: "Warehouse Management",
                                                    desc: "Efficient warehouse operations and fulfillment.",
                                                    items: ["Pick, Pack & Ship", "Cycle Counting", "Cross-docking & 3PL Integration"]
                                                },
                                                {
                                                    title: "Logistics & Transportation",
                                                    desc: "Optimize shipping and delivery operations.",
                                                    items: ["Route Optimization", "Carrier Management", "Real-time Tracking"]
                                                }
                                            ].map((activity, index) => (
                                                <div key={index} className="col-md-6" style={{ marginBottom: "30px" }}>
                                                    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "30px", height: "100%" }}>
                                                        <h3 style={{ fontSize: "1.25rem", color: "#fff", marginBottom: "15px" }}>
                                                            <span style={{ color: "#fc9038" }}>●</span> {activity.title}
                                                        </h3>
                                                        <p style={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.7)", marginBottom: "15px" }}>{activity.desc}</p>
                                                        <ul style={{ listStyle: "none", padding: 0 }}>
                                                            {activity.items.map((item, i) => (
                                                                <li key={i} style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.6)", padding: "5px 0" }}>→ {item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Why Us Section */}
                        <section style={{ padding: "80px 0", background: "#0a0a0a" }}>
                            <div className="container">
                                <div className="text-center" style={{ marginBottom: "50px" }}>
                                    <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px" }}>Why Us</span>
                                    <h2 style={{ fontSize: "2.5rem", margin: "15px 0 20px", color: "#fff" }}>Why Choose AquarLabs for Supply Chain?</h2>
                                    <p style={{ fontSize: "1rem", color: "rgba(255,255,255,0.6)", maxWidth: "700px", margin: "0 auto" }}>
                                        Deep supply chain expertise with cutting-edge technology for measurable results.
                                    </p>
                                </div>
                                <div className="row">
                                    {[
                                        { title: "AI-Powered Forecasting", desc: "Machine learning models that improve demand accuracy by 30%+." },
                                        { title: "Real-Time Visibility", desc: "Track orders, shipments, and inventory across the entire network." },
                                        { title: "Risk Management", desc: "Identify and mitigate supply chain risks before they impact operations." },
                                        { title: "Supplier Collaboration", desc: "Portals for seamless communication with suppliers and partners." },
                                        { title: "Cost Reduction", desc: "Typical clients see 15-25% reduction in supply chain costs." },
                                        { title: "Sustainability Tracking", desc: "Monitor and report on environmental impact across the supply chain." }
                                    ].map((item, index) => (
                                        <div key={index} className="col-lg-6" style={{ marginBottom: "20px" }}>
                                            <div style={{
                                                background: "rgba(20, 20, 20, 0.8)",
                                                border: "1px solid rgba(252, 144, 56, 0.4)",
                                                borderRadius: "16px",
                                                padding: "28px 30px",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: "15px"
                                            }}>
                                                <div style={{
                                                    minWidth: "40px",
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    background: "rgba(252, 144, 56, 0.1)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect x="1" y="6" width="4" height="6" stroke="#fc9038" strokeWidth="2" />
                                                        <rect x="10" y="3" width="4" height="9" stroke="#fc9038" strokeWidth="2" />
                                                        <rect x="19" y="8" width="4" height="4" stroke="#fc9038" strokeWidth="2" />
                                                        <path d="M5 9H10" stroke="#fc9038" strokeWidth="2" />
                                                        <path d="M14 7L19 10" stroke="#fc9038" strokeWidth="2" />
                                                        <path d="M3 12V18H21V12" stroke="#fc9038" strokeWidth="2" strokeLinecap="round" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h3 style={{ fontSize: "1.1rem", color: "#fff", marginBottom: "8px", fontWeight: "600" }}>{item.title}</h3>
                                                    <p style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.6)", margin: 0, lineHeight: "1.6" }}>{item.desc}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* CTA Section */}
                        <section style={{ padding: "80px 0", background: "linear-gradient(135deg, rgba(252, 144, 56, 0.1) 0%, rgba(0, 2, 15, 0.5) 100%)" }}>
                            <div className="container text-center">
                                <h2 style={{ fontSize: "2.5rem", color: "#fff", marginBottom: "20px" }}>Transform Your Supply Chain</h2>
                                <p style={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.7)", marginBottom: "30px" }}>
                                    Discover how our solutions can optimize your end-to-end supply chain operations.
                                </p>
                                <Link to="/contact-us" className="thm-btn agency-btn">
                                    <span className="text">Contact Us Today</span>
                                </Link>
                            </div>
                        </section>
                    </main>
                    <Footer />
                    <Scrollbar />
                </div>
            </div>
        </Fragment>
    );
};

export default SupplyChainPage;
